import { DocumentMeta } from '../meta';
import { AmortizationStatus } from '../static';
import { isFilmAmortizedCondition } from './conditions';
import { getRightConditions, Right } from './right';
import { TitleState } from './state';
import { WaterfallSource } from './waterfall';
import { getCurrentAmortization } from './conditions';
import { toFixedPercentage } from '../utils';

export interface Amortization {
  _meta?: DocumentMeta;
  id: string;
  waterfallId: string;
  name: string;
  contractIds: string[];
  status: AmortizationStatus;
  filmCost: number;
  financing: number;
  poolName: string;
}

export function createAmortization(params: Partial<Amortization>): Amortization {
  return {
    id: '',
    status: 'draft',
    name: '',
    waterfallId: '',
    contractIds: [],
    filmCost: 0,
    financing: 0,
    poolName: '',
    ...params
  };
}

/**
 * Get all active amortizations linked to the given rights
 * @param amortizations 
 * @param rights 
 * @returns 
 */
export function getActiveAmortizations(amortizations: Amortization[], rights: Right[]) {

  const amortizationIds: string[] = [];
  for (const right of rights) {
    const conditions = getRightConditions(right);
    for (const condition of conditions) {
      if (isFilmAmortizedCondition(condition)) amortizationIds.push((condition.payload.target as { id: string }).id);
    }
  }

  return amortizations.filter(a => a.status === 'applied' && amortizationIds.includes(a.id));
}

export function getAmortizationResults(state: TitleState, amortizations: Amortization[], rights: Right[], sources: Partial<WaterfallSource>[], toCurrency: (value: number) => string) {

  const activeAmortizations = getActiveAmortizations(amortizations, rights);
  return activeAmortizations.map(amortization => {
    const current = getCurrentAmortization(state, amortization.id);
    const percent = current / amortization.filmCost;

    const poolTurnover = state.pools[amortization.poolName]?.turnover;
    const sourceState = Object.values(state.sources);

    const perSource: Record<string, number> = {};
    if (poolTurnover) {
      for (const [incomeId, turnover] of Object.entries(poolTurnover.history)) {
        const source = sourceState.find(s => s.incomeIds.includes(incomeId));
        const sourceName = source ? sources.find(s => s.id === source.id)?.name : undefined;
        if (sourceName) {
          if (!perSource[sourceName]) perSource[sourceName] = 0;
          perSource[sourceName] += turnover;
        }
      }
    }
    const tooltipSuffix = activeAmortizations.length > 1 ? `(${amortization.name})` : '';

    return {
      ...amortization,
      state: state.amortizations[amortization.id],
      turnover: {
        total: poolTurnover?.calculated ?? 0,
        perSource
      },
      current: toCurrency(current),
      filmCost: toCurrency(amortization.filmCost),
      percent: toFixedPercentage(percent),
      isAmortized: current > amortization.filmCost,
      tooltip: current > amortization.filmCost ? `Le film est amorti ${tooltipSuffix}` : `Le film n'est pas amorti ${tooltipSuffix}`,
      leftToRecoup: amortization.filmCost - current > 0 ? toCurrency(amortization.filmCost - current) : undefined
    };
  });

}
